<template>
  <v-app>
    <!-- «Просмотр как пользователь» (task 60): the admin sees the user's system, read only -->
    <v-system-bar v-if="viewingAs" color="warning" height="40" class="justify-center view-as-bar">
      <v-icon size="18" class="mr-2">mdi-eye</v-icon>
      <span>Вы смотрите как <strong>{{ viewedEmail }}</strong> — только просмотр, изменения запрещены</span>
      <v-btn size="small" variant="flat" color="white" class="ml-4" :loading="leavingView" @click="stopViewAs">
        Вернуться к себе
      </v-btn>
    </v-system-bar>

    <!-- App Bar (only for authenticated users) -->
    <v-app-bar
      v-if="isLoggedIn && isAuthInitialized"
      app
      elevation="1"
      color="white"
      :height="90"
    >
      <v-toolbar-title class="d-flex align-center">
        <router-link to="/" class="d-flex align-center app-logo" title="Следограф">
          <img src="/logo-horizontal-512.png" alt="Следограф" class="app-logo-img" />
        </router-link>
      </v-toolbar-title>

      <v-spacer></v-spacer>

      <!-- Alert in center -->
      <div class="position-absolute" style="left: 50%; transform: translateX(-50%);">
        <v-slide-y-transition>
          <v-alert
            v-if="showAlert"
            :type="messageType"
            closable
            density="compact"
            @click:close="showAlert = false"
            class="ma-0"
          >
            {{ message }}
          </v-alert>
        </v-slide-y-transition>
      </div>

      <v-spacer></v-spacer>

      <!-- The space: personal or a team (task 59), only for members of a team -->
      <space-switcher />

      <!-- Notifications: the bell and the windows on sign-in -->
      <notification-bell />

      <!-- Header menu -->
      <header-menu />
    </v-app-bar>

    <!-- App Bar for guests: logo leads home, sign-in links on the right -->
    <v-app-bar
      v-else-if="isAuthInitialized"
      app
      elevation="1"
      color="white"
      :height="90"
    >
      <v-toolbar-title class="d-flex align-center">
        <router-link to="/" class="d-flex align-center app-logo" title="Следограф">
          <img src="/logo-horizontal-512.png" alt="Следограф" class="app-logo-img" />
        </router-link>
      </v-toolbar-title>

      <v-spacer></v-spacer>

      <v-btn variant="text" size="small" to="/signin">Вход</v-btn>
      <v-btn variant="text" size="small" to="/signup" class="mr-2">Регистрация</v-btn>
    </v-app-bar>

    <!-- Footer is fixed at the bottom of the window on every page -->
    <app-footer />

    <!-- Navigation Drawer (only for authenticated users) -->
    <v-navigation-drawer
      v-if="isLoggedIn && isAuthInitialized"
      v-model="drawer"
      :rail="rail"
      :width="drawerWidth"
      permanent
      @click="rail = false"
    >
      <!-- Ручка изменения ширины меню (ширина хранится в cookie nav_drawer_width) -->
      <div v-if="!rail" class="drawer-resizer" title="Потяните, чтобы изменить ширину меню" @mousedown.prevent.stop="startDrawerResize"></div>
      <v-list-item :class="rail ? 'justify-center' : 'justify-end'">
        <v-btn
          variant="text"
          icon
          @click.stop="rail = !rail"
        >
          <v-icon>{{ rail ? 'mdi-menu' : 'mdi-menu-open' }}</v-icon>
        </v-btn>
      </v-list-item>

      <v-divider></v-divider>

      <navigation-menu />
    </v-navigation-drawer>

    <!-- Main Content -->
    <v-main>
      <v-fade-transition mode="out-in">
        <!-- Loading state -->
        <v-container
          v-if="!isAuthInitialized"
          key="loading"
          class="fill-height"
        >
          <v-row
            align="center"
            justify="center"
          >
            <v-col
              cols="auto"
              class="text-center"
            >
              <v-progress-circular
                indeterminate
                color="primary"
                size="64"
              ></v-progress-circular>
              <p class="mt-4">Инициализация...</p>
            </v-col>
          </v-row>
        </v-container>

        <!-- Authenticated layout -->
        <v-container
          v-else-if="isLoggedIn && isAuthInitialized"
          key="authenticated"
          fluid
          class="pa-4"
        >
          <router-view @show-message="showMessage"></router-view>
        </v-container>

        <!-- Unauthenticated layout -->
        <router-view
          v-else
          key="unauthenticated"
          @show-message="showMessage"
        ></router-view>
      </v-fade-transition>
    </v-main>

    <!-- Filter Panel (right side) -->
    <FilterPanel />
    
    <!-- Token refresh indicator -->
    <TokenRefreshIndicator />

    <!-- «Сообщить об ошибке» on every page after sign-in -->
    <FeedbackButton v-if="isLoggedIn && isAuthInitialized" @show-message="showMessage" />
  </v-app>
</template>

<script>
import { mapState } from 'vuex';
import apiClient from '@/apiClient';
import { checkAuth } from '@/authService';
import { sessionIdle, startActivityTracking } from '@/utils/activity';
import TokenRefreshIndicator from "@/components/TokenRefreshIndicator.vue";
import NavigationMenu from "@/components/NavigationMenu.vue";
import HeaderMenu from "@/components/HeaderMenu.vue";
import NotificationBell from "@/components/NotificationBell.vue";
import SpaceSwitcher from "@/components/SpaceSwitcher.vue";
import FilterPanel from "@/components/FilterPanel.vue";
import AppFooter from "@/components/AppFooter.vue";
import FeedbackButton from "@/components/FeedbackButton.vue";

export default {
  name: 'App',
  components: {
    TokenRefreshIndicator,
    NavigationMenu,
    HeaderMenu,
    NotificationBell,
    SpaceSwitcher,
    FilterPanel,
    AppFooter,
    FeedbackButton
  },
  data() {
    return {
      leavingView: false,
      showAlert: false,
      messageType: 'success',
      message: '',
      authInterval: null, // Для хранения идентификатора интервала
      alertTimeout: null, // Для хранения таймера автоскрытия alert
      drawer: true,
      drawerWidth: this.loadDrawerWidth(),
      rail: this.loadSidebarState()
    };
  },
  computed: {
    ...mapState(['isLoggedIn', 'authInitialized']),
    isAuthInitialized() {
      return this.authInitialized;
    },
    viewingAs() {
      const info = this.$store.state.userInfo
      return this.isLoggedIn && info && info.viewing_as ? info.viewing_as : null
    },
    viewedEmail() {
      return this.$store.state.userInfo?.email || ''
    },
  },
  watch: {
    // Запускаем/останавливаем проверку при изменении статуса логина
    isLoggedIn(newValue, oldValue) {
      if (newValue && this.isAuthInitialized) {
        this.startAuthCheck();
      } else {
        this.stopAuthCheck();
      }
    },
    // Также запускаем проверку когда авторизация инициализирована
    isAuthInitialized(newValue) {
      if (newValue && this.isLoggedIn) {
        this.startAuthCheck();
      }
    },
    // Сохраняем состояние sidebar при изменении
    rail() {
      this.saveSidebarState();
    }
  },
  mounted() {
    startActivityTracking();
    // a hidden tab may have its timers paused by the browser: check the idle limit when it is shown
    document.addEventListener('visibilitychange', this.onVisible);
    // Инициальный запуск проверки если пользователь уже авторизован
    if (this.isLoggedIn && this.isAuthInitialized) {
      this.startAuthCheck();
    }
  },
  beforeUnmount() {
    document.removeEventListener('visibilitychange', this.onVisible);
    this.stopDrawerResize();
    this.stopAuthCheck();
    if (this.alertTimeout) {
      clearTimeout(this.alertTimeout);
    }
  },
  async created() {
    // Auth initialization is already handled in main.js
    // Removed duplicate call to prevent conflicts
  },
  methods: {
    async stopViewAs() {
      this.leavingView = true
      try {
        await apiClient.post('/admin/view-as/stop')
      } finally {
        // the whole page anew as the admin himself
        window.location.assign('/admin/users')
      }
    },
    loadDrawerWidth() {
      try {
        const raw = document.cookie.split(';').map(c => c.trim()).find(c => c.startsWith('nav_drawer_width='));
        const w = raw ? parseInt(raw.slice('nav_drawer_width='.length), 10) : NaN;
        return Number.isFinite(w) ? Math.min(600, Math.max(200, w)) : 256;
      } catch (e) { return 256; }
    },
    saveDrawerWidth() {
      try { document.cookie = `nav_drawer_width=${this.drawerWidth};path=/;max-age=31536000`; } catch (e) { /* без cookie */ }
    },
    startDrawerResize(e) {
      this._drawerStartX = e.clientX; this._drawerStartW = this.drawerWidth;
      this._onDrawerMove = (ev) => { this.drawerWidth = Math.min(600, Math.max(200, this._drawerStartW + (ev.clientX - this._drawerStartX))); };
      this._onDrawerUp = () => { this.stopDrawerResize(); this.saveDrawerWidth(); };
      window.addEventListener('mousemove', this._onDrawerMove); window.addEventListener('mouseup', this._onDrawerUp);
      document.body.style.userSelect = 'none';
    },
    stopDrawerResize() {
      if (this._onDrawerMove) window.removeEventListener('mousemove', this._onDrawerMove);
      if (this._onDrawerUp) window.removeEventListener('mouseup', this._onDrawerUp);
      this._onDrawerMove = null; this._onDrawerUp = null; document.body.style.userSelect = '';
    },
    async onVisible() {
      if (document.visibilityState === 'visible' && this.isLoggedIn && this.isAuthInitialized && sessionIdle()) {
        this.stopAuthCheck();
        await this.$store.dispatch('logoutIdle');
        if (this.$route.path !== '/signin') this.$router.push('/signin');
      }
    },
    startAuthCheck() {
      // Останавливаем существующий интервал если есть
      this.stopAuthCheck();
      
      this.authInterval = setInterval(async () => {
        if (this.isLoggedIn && this.isAuthInitialized) {
          // minutes without an action: the session ends here, the token is not refreshed
          if (sessionIdle()) {
            this.stopAuthCheck();
            await this.$store.dispatch('logoutIdle');
            if (this.$route.path !== '/signin') {
              this.$router.push('/signin');
            }
            return;
          }
          try {
            const stillAuthorized = await checkAuth();
            if (!stillAuthorized) {
              // Пользователь больше не авторизован
              this.stopAuthCheck();
              await this.$store.dispatch('logout');
              if (this.$route.path !== '/signin') {
                this.$router.push('/signin');
              }
            }
          } catch (error) {
            // При ошибке сети не выходим из системы, просто пропускаем проверку
          }
        }
      }, 30000); // Проверка каждые 30 секунд
    },
    
    stopAuthCheck() {
      if (this.authInterval) {
        clearInterval(this.authInterval);
        this.authInterval = null;
      }
    },
    
    showMessage(message, isError) {
      this.messageType = isError ? 'error' : 'success';
      this.message = message;
      this.showAlert = true;

      // Clear previous timeout if exists
      if (this.alertTimeout) {
        clearTimeout(this.alertTimeout);
      }

      // Auto-hide alert after 5 seconds
      this.alertTimeout = setTimeout(() => {
        this.showAlert = false;
        this.alertTimeout = null;
      }, 5000);
    },

    loadSidebarState() {
      const saved = localStorage.getItem('sidebar-collapsed');
      return saved !== null ? saved === 'true' : true;
    },

    saveSidebarState() {
      localStorage.setItem('sidebar-collapsed', this.rail);
    }
  }
};
</script>



<style scoped>
.drawer-resizer { position: absolute; top: 0; right: 0; width: 6px; height: 100%; cursor: col-resize; z-index: 5; }
.drawer-resizer:hover { background: rgba(25,118,210,0.25); }
/* Custom styles for Vuetify components */
.app-logo { height: 78px; }
.app-logo-img { height: 78px; width: auto; display: block; }

/* Style for alert positioning */
.position-absolute {
  position: absolute;
}
</style>
